import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { AuthContext } from 'context/Auth';
import { db } from 'services/firebase';
import ConfirmDialog from 'components/common/ConfirmDialog';
import MasterChartBackButton from 'components/dashboard/healthRecords/MasterChartBackButton';
import DashboardPageHeader from 'components/common/DashboardPageHeader';
import DataLoadErrorAlert from 'components/common/DataLoadErrorAlert';
import {
  RecordActions,
  RecordCard,
  RecordCardStack,
  joinDetail,
} from 'components/dashboard/healthRecords/RecordList';

const COMMON_PROCEDURES = [
  'Appendectomy', 'Cholecystectomy', 'Cesarean Section', 'Hysterectomy',
  'Knee Replacement', 'Hip Replacement', 'Coronary Artery Bypass (CABG)',
  'Hernia Repair', 'Tonsillectomy', 'Cataract Surgery', 'Colonoscopy',
  'Endoscopy', 'Laparoscopy', 'Spinal Fusion', 'Mastectomy',
];

const SurgicalHistoryPage = () => {
  const { user } = useContext(AuthContext);

  const [surgeries, setSurgeries] = useState([]);
  const [fullHealthRecords, setFullHealthRecords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { control, handleSubmit, reset, setValue } = useForm({
    defaultValues: { procedure: '', year: '', hospital: '', notes: '' },
  });

  const load = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    setLoadError(false);
    try {
      const doc = await db.collection('users').doc(user.uid).get();
      const hr = doc.data()?.healthRecords || {};
      setFullHealthRecords(hr);
      setSurgeries(hr.psh || []);
    } catch {
      // An empty list here would read as "none", and adding to it would replace
      // the stored list, so the list and Add stay hidden until a retry succeeds.
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    load();
  }, [load]);

  const saveToFirestore = useCallback(
    async (updated) => {
      const merged = {
        ...(fullHealthRecords ?? {}),
        psh: updated,
        updatedAt: new Date().toISOString(),
      };
      await db
        .collection('users')
        .doc(user.uid)
        .set({ healthRecords: merged }, { merge: true });
      setFullHealthRecords(merged);
    },
    [user?.uid, fullHealthRecords]
  );

  const openDialog = (index = -1) => {
    setEditingIndex(index);
    if (index >= 0) {
      const s = surgeries[index];
      reset({
        procedure: s.procedure ?? '',
        year: s.year ?? '',
        hospital: s.hospital ?? '',
        notes: s.notes ?? '',
      });
    } else {
      reset({ procedure: '', year: '', hospital: '', notes: '' });
    }
    setDialogOpen(true);
  };

  const onDialogSubmit = handleSubmit(async (data) => {
    setSaving(true);
    setError(null);
    try {
      const toSave = {
        procedure: data.procedure.trim(),
        year: data.year ? String(data.year).trim() : '',
        hospital: data.hospital.trim(),
        notes: data.notes.trim(),
      };
      const updated = [...surgeries];
      if (editingIndex >= 0) {
        updated[editingIndex] = toSave;
      } else {
        updated.push(toSave);
      }
      updated.sort((a, b) => (parseInt(b.year) || 0) - (parseInt(a.year) || 0));
      await saveToFirestore(updated);
      setSurgeries(updated);
      setDialogOpen(false);
      setSuccessMsg(editingIndex >= 0 ? 'Entry updated.' : 'Surgery added.');
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  });

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const updated = surgeries.filter((_, i) => i !== pendingDelete.index);
      setSurgeries(updated);
      await saveToFirestore(updated);
      setSuccessMsg(`Removed "${pendingDelete.label}".`);
      setPendingDelete(null);
    } catch {
      setError('Failed to remove. Please try again.');
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="inner">
      <DashboardPageHeader
        title="Surgical History"
        subtitle="Add any surgeries or procedures you've had, so doctors know your history before they examine or treat you."
      />

      {loadError && <DataLoadErrorAlert message="We couldn't load your surgical history. Please retry before assuming you have none." onRetry={load} />}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ mb: 2 }}>
          {successMsg}
        </Alert>
      )}

      {!loadError && (
        <>

          <RecordCardStack loading={loading}>
            {surgeries.map((s, i) => (
              <RecordCard
                key={i}
                title={s.procedure}
                detail={joinDetail(s.year, s.hospital, s.notes)}
                onEdit={() => openDialog(i)}
                onDelete={() => setPendingDelete({ index: i, label: s.procedure })}
                editLabel={`Edit procedure: ${s.procedure}`}
                deleteLabel={`Delete procedure: ${s.procedure}`}
              />
            ))}
          </RecordCardStack>

          <RecordActions
            addLabel="Add a Surgery"
            onAdd={() => openDialog()}
            disabled={loading}
            spaced={!loading && surgeries.length > 0}
          >
            <MasterChartBackButton />
          </RecordActions>
        </>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingIndex >= 0 ? 'Edit Entry' : 'Add Surgery / Procedure'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Controller
              name="procedure"
              control={control}
              rules={{ required: 'Procedure name is required' }}
              render={({ field, fieldState: { error: fieldError } }) => (
                <TextField
                  {...field}
                  label="Procedure"
                  fullWidth
                  placeholder="e.g., Appendectomy"
                  error={!!fieldError}
                  helperText={fieldError?.message}
                />
              )}
            />

            <Controller
              name="year"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Year"
                  type="number"
                  fullWidth
                  placeholder={String(new Date().getFullYear())}
                  inputProps={{ min: 1900, max: new Date().getFullYear() }}
                />
              )}
            />

            <Controller
              name="hospital"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Hospital / Facility"
                  fullWidth
                  placeholder="Where was the procedure performed?"
                />
              )}
            />

            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Notes"
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Complications, outcomes, or other details (optional)"
                />
              )}
            />

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                gutterBottom
              >
                Common procedures:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {COMMON_PROCEDURES.slice(0, 8).map((name) => (
                  <Chip
                    key={name}
                    label={name}
                    size="small"
                    variant="outlined"
                    onClick={() => setValue('procedure', name)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            onClick={onDialogSubmit}
            loading={saving}
            disabled={saving}
          >
            {editingIndex >= 0 ? 'Update' : 'Add'}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title="Remove Entry"
        message={`Remove "${pendingDelete?.label}"?`}
        confirmLabel="Remove"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
        onExited={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default SurgicalHistoryPage;
